import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, TextField, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import type { BrandStrategy, Envergure, LocationScope, IndustryCode } from '@traaker/shared';
import { BRAND_STRATEGY_LIMITS, INDUSTRY_CODES, industryLabels, isIndustryCode } from '@traaker/shared';

// Shared Brand Knowledge editing primitives.
//
// Extracted from BrandsSettings so both the brand-settings DiscoveryDialog and
// the onboarding wizard render the SAME section editors over a `BrandStrategy`
// draft. Keep this module presentational — it owns no data fetching.
//
// BRAND_STRATEGY_LIMITS is imported from @traaker/shared, never re-declared:
// this module used to hand-copy it, and the copy silently drifted from the
// server's validator (keywords.maxCharsPerItem stayed 60 after the shared cap
// rose to 100), so inputs capped shorter than the API would have accepted.

// i18n key map — resolved with t() at render (labels live in module scope).
export const ENVERGURE_LABEL_KEYS: Record<Envergure, string> = {
  'hyper-local': 'brandStrategy.envergure.hyperLocal',
  'multi-local': 'brandStrategy.envergure.multiLocal',
  'mono-country': 'brandStrategy.envergure.monoCountry',
  'multi-country': 'brandStrategy.envergure.multiCountry',
  global: 'brandStrategy.envergure.global',
};

// NOTE: kept as literal French — SCOPE_LABELS is imported and rendered raw by
// BrandsSettings.tsx (out of this batch's scope); converting it to i18n keys
// would require editing that file. Revisit when BrandsSettings is i18n'd.
export const SCOPE_LABELS: Record<LocationScope, string> = {
  worldwide: 'Mondial',
  national: 'National',
  city: 'Ville',
  address: 'Adresse',
};

export type SectionKey =
  | 'identity'
  | 'market'
  | 'locations'
  | 'valueProp'
  | 'scenarios'
  | 'keywords'
  | 'customerQuestions'
  | 'customerObjections'
  | 'avoidTopics'
  | 'languagesMaturity'
  | 'existingAssets';

export interface SectionDef {
  key: SectionKey;
  /** i18n keys — resolve with t() at render (definitions live in module scope). */
  titleKey: string;
  descriptionKey: string;
  usedByKey: string;
  isEmpty: (s: BrandStrategy) => boolean;
}

export const SECTIONS: SectionDef[] = [
  {
    key: 'identity',
    titleKey: 'strategySections.identity.title',
    descriptionKey: 'strategySections.identity.description',
    usedByKey: 'strategySections.usedBy.emBpVeille',
    isEmpty: (s) => !s.industry && !(s.aliases && s.aliases.length > 0),
  },
  {
    key: 'market',
    titleKey: 'strategySections.market.title',
    descriptionKey: 'strategySections.market.description',
    usedByKey: 'strategySections.usedBy.bpQueryGen',
    isEmpty: (s) => !s.clientPersonas && !s.businessModel && !s.geographicFocus,
  },
  {
    key: 'locations',
    titleKey: 'strategySections.locations.title',
    descriptionKey: 'strategySections.locations.description',
    usedByKey: 'strategySections.usedBy.allAuditsGeo',
    // Always considered "empty" from strategy POV — locations live on the brand object, not on strategy.
    isEmpty: () => true,
  },
  {
    key: 'valueProp',
    titleKey: 'strategySections.valueProp.title',
    descriptionKey: 'strategySections.valueProp.description',
    usedByKey: 'strategySections.usedBy.emBpVeille',
    isEmpty: (s) => !s.description && !(s.differentiators && s.differentiators.length > 0),
  },
  {
    key: 'scenarios',
    titleKey: 'strategySections.scenarios.title',
    descriptionKey: 'strategySections.scenarios.description',
    usedByKey: 'strategySections.usedBy.bpVeille',
    isEmpty: (s) => !(s.scenarios && s.scenarios.length > 0),
  },
  {
    key: 'keywords',
    titleKey: 'strategySections.keywords.title',
    descriptionKey: 'strategySections.keywords.description',
    usedByKey: 'strategySections.usedBy.emBpVeille',
    isEmpty: (s) => !(s.keywords && s.keywords.length > 0),
  },
  {
    key: 'customerQuestions',
    titleKey: 'strategySections.customerQuestions.title',
    descriptionKey: 'strategySections.customerQuestions.description',
    usedByKey: 'strategySections.usedBy.bpReferralVeille',
    isEmpty: (s) => !(s.customerQuestions && s.customerQuestions.length > 0),
  },
  {
    key: 'customerObjections',
    titleKey: 'strategySections.customerObjections.title',
    descriptionKey: 'strategySections.customerObjections.description',
    usedByKey: 'strategySections.usedBy.emValidationBpNegative',
    isEmpty: (s) => !(s.customerObjections && s.customerObjections.length > 0),
  },
  {
    key: 'avoidTopics',
    titleKey: 'strategySections.avoidTopics.title',
    descriptionKey: 'strategySections.avoidTopics.description',
    usedByKey: 'strategySections.usedBy.emFilterBp',
    isEmpty: (s) => !(s.avoidTopics && s.avoidTopics.length > 0),
  },
  {
    key: 'languagesMaturity',
    titleKey: 'strategySections.languagesMaturity.title',
    descriptionKey: 'strategySections.languagesMaturity.description',
    usedByKey: 'strategySections.usedBy.bpEm',
    isEmpty: (s) => !(s.targetLanguages && s.targetLanguages.length > 0) && !s.envergure,
  },
  {
    key: 'existingAssets',
    titleKey: 'strategySections.existingAssets.title',
    descriptionKey: 'strategySections.existingAssets.description',
    usedByKey: 'strategySections.usedBy.emReliableSources',
    isEmpty: (s) =>
      !s.existingAssets?.wikipediaUrl &&
      !s.existingAssets?.pressKitUrl &&
      !(s.existingAssets?.notableMentions && s.existingAssets.notableMentions.length > 0),
  },
];

export interface PendingLocation {
  label: string;
  zone: string;
  scope: LocationScope | '';
}

// Strip empty strings / empty arrays so we don't overwrite existing brand data with nothing
export function pruneEmpty(s: BrandStrategy): Partial<BrandStrategy> {
  const out: Partial<BrandStrategy> = {};
  if (s.industry) out.industry = s.industry;
  if (s.aliases && s.aliases.length > 0) out.aliases = s.aliases;
  if (s.clientPersonas) out.clientPersonas = s.clientPersonas;
  if (s.businessModel) out.businessModel = s.businessModel;
  if (s.geographicFocus) out.geographicFocus = s.geographicFocus;
  if (s.description) out.description = s.description;
  if (s.differentiators && s.differentiators.length > 0) out.differentiators = s.differentiators;
  if (s.scenarios && s.scenarios.length > 0) out.scenarios = s.scenarios;
  if (s.keywords && s.keywords.length > 0) out.keywords = s.keywords;
  if (s.customerQuestions && s.customerQuestions.length > 0) out.customerQuestions = s.customerQuestions;
  if (s.customerObjections && s.customerObjections.length > 0) out.customerObjections = s.customerObjections;
  if (s.avoidTopics && s.avoidTopics.length > 0) out.avoidTopics = s.avoidTopics;
  if (s.targetLanguages && s.targetLanguages.length > 0) out.targetLanguages = s.targetLanguages;
  if (s.envergure) out.envergure = s.envergure;
  if (s.existingAssets) {
    const a = s.existingAssets;
    const AL = BRAND_STRATEGY_LIMITS.existingAssets;
    const trimmed: BrandStrategy['existingAssets'] = {};
    if (a.wikipediaUrl) trimmed!.wikipediaUrl = a.wikipediaUrl.slice(0, AL.wikipediaUrl);
    if (a.pressKitUrl) trimmed!.pressKitUrl = a.pressKitUrl.slice(0, AL.pressKitUrl);
    // Clamp on BOTH axes, matching the server's validator: the onboarding wizard
    // renders no existingAssets editor, so a discovery-supplied over-limit value
    // would otherwise 400 the strategy PATCH on a screen where the user can't see
    // (let alone trim) the field.
    if (a.notableMentions && a.notableMentions.length > 0) {
      trimmed!.notableMentions = a.notableMentions
        .slice(0, AL.notableMentions.maxItems)
        .map((m) => m.slice(0, AL.notableMentions.maxCharsPerItem));
    }
    if (Object.keys(trimmed!).length > 0) out.existingAssets = trimmed;
  }
  if (s.audienceMix) out.audienceMix = s.audienceMix;
  if (s.objectiveMix) out.objectiveMix = s.objectiveMix;
  // Brand working language (AI-output-prose language) — set once at onboarding.
  // Must survive the prune or it never reaches the server and every downstream
  // generation (GEO strategic angle, example prompts, agents) falls back to fr.
  if (s.workingLanguage) out.workingLanguage = s.workingLanguage;
  return out;
}

export function SectionEditor({
  sectionKey,
  draft,
  setDraft,
}: {
  sectionKey: SectionKey;
  draft: BrandStrategy;
  setDraft: React.Dispatch<React.SetStateAction<BrandStrategy>>;
}) {
  const { t, i18n } = useTranslation();
  const industryLabelMap = industryLabels(i18n.language);
  switch (sectionKey) {
    case 'identity':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', mb: 0.5, display: 'block' }}>
              {t('strategySections.field.industry')}
            </Typography>
            <Select
              size="small"
              fullWidth
              displayEmpty
              value={isIndustryCode(draft.industry) ? draft.industry : ''}
              onChange={(e) =>
                setDraft({ ...draft, industry: (e.target.value || undefined) as IndustryCode | undefined })
              }
              renderValue={(v) =>
                v ? industryLabelMap[v as IndustryCode] : <span style={{ color: '#9aa' }}>{t('strategySections.industryPlaceholder')}</span>
              }
            >
              {INDUSTRY_CODES.map((code) => (
                <MenuItem key={code} value={code}>{industryLabelMap[code]}</MenuItem>
              ))}
            </Select>
            {draft.industryLegacy && !isIndustryCode(draft.industry) && (
              <Typography variant="caption" sx={{ color: 'warning.main', mt: 0.5, display: 'block' }}>
                {t('strategySections.industryLegacy', { legacy: draft.industryLegacy })}
              </Typography>
            )}
          </Box>
          <ChipEditor
            label={t('strategySections.field.aliases')}
            items={draft.aliases || []}
            onChange={(items) => setDraft({ ...draft, aliases: items })}
            placeholder={t('strategySections.placeholder.enterToAdd')}
            limits={BRAND_STRATEGY_LIMITS.aliases}
          />
        </Box>
      );
    case 'market':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('strategySections.field.clientPersonas')}
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={draft.clientPersonas || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                clientPersonas: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.clientPersonas),
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.clientPersonas }}
            helperText={`${(draft.clientPersonas || '').length} / ${BRAND_STRATEGY_LIMITS.clientPersonas}`}
          />
          <Select
            size="small"
            fullWidth
            value={draft.businessModel || ''}
            onChange={(e) =>
              setDraft({ ...draft, businessModel: (e.target.value as any) || undefined })
            }
            displayEmpty
          >
            <MenuItem value="">{t('strategySections.businessModel.undefined')}</MenuItem>
            <MenuItem value="b2b">B2B</MenuItem>
            <MenuItem value="b2c">B2C</MenuItem>
            <MenuItem value="both">B2B & B2C</MenuItem>
          </Select>
          <TextField
            label={t('strategySections.field.geographicFocus')}
            size="small"
            fullWidth
            value={draft.geographicFocus || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                geographicFocus: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.geographicFocus),
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.geographicFocus }}
            helperText={`${(draft.geographicFocus || '').length} / ${BRAND_STRATEGY_LIMITS.geographicFocus}`}
          />
        </Box>
      );
    case 'valueProp':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('strategySections.field.description')}
            size="small"
            fullWidth
            multiline
            minRows={3}
            value={draft.description || ''}
            onChange={(e) =>
              setDraft({ ...draft, description: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.description) })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.description }}
            helperText={`${(draft.description || '').length} / ${BRAND_STRATEGY_LIMITS.description}`}
          />
          <ChipEditor
            label={t('strategySections.field.differentiators')}
            items={draft.differentiators || []}
            onChange={(items) => setDraft({ ...draft, differentiators: items })}
            placeholder={t('strategySections.placeholder.enterToAdd')}
            limits={BRAND_STRATEGY_LIMITS.differentiators}
          />
        </Box>
      );
    case 'scenarios':
      return (
        <ChipEditor
          label={t('strategySections.field.scenarios')}
          items={draft.scenarios || []}
          onChange={(items) => setDraft({ ...draft, scenarios: items })}
          placeholder={t('strategySections.placeholder.scenarios')}
          limits={BRAND_STRATEGY_LIMITS.scenarios}
        />
      );
    case 'keywords':
      return (
        <ChipEditor
          label={t('strategySections.field.keywords')}
          items={draft.keywords || []}
          onChange={(items) => setDraft({ ...draft, keywords: items })}
          placeholder={t('strategySections.placeholder.keywords')}
          limits={BRAND_STRATEGY_LIMITS.keywords}
        />
      );
    case 'customerQuestions':
      return (
        <ChipEditor
          label={t('strategySections.field.customerQuestions')}
          items={draft.customerQuestions || []}
          onChange={(items) => setDraft({ ...draft, customerQuestions: items })}
          placeholder={t('strategySections.placeholder.customerQuestions')}
          limits={BRAND_STRATEGY_LIMITS.customerQuestions}
        />
      );
    case 'customerObjections':
      return (
        <ChipEditor
          label={t('strategySections.field.customerObjections')}
          items={draft.customerObjections || []}
          onChange={(items) => setDraft({ ...draft, customerObjections: items })}
          placeholder={t('strategySections.placeholder.customerObjections')}
          limits={BRAND_STRATEGY_LIMITS.customerObjections}
        />
      );
    case 'avoidTopics':
      return (
        <ChipEditor
          label={t('strategySections.field.avoidTopics')}
          items={draft.avoidTopics || []}
          onChange={(items) => setDraft({ ...draft, avoidTopics: items })}
          placeholder={t('strategySections.placeholder.enterToAdd')}
          limits={BRAND_STRATEGY_LIMITS.avoidTopics}
        />
      );
    case 'languagesMaturity':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <ChipEditor
            label={t('strategySections.field.targetLanguages')}
            items={draft.targetLanguages || []}
            onChange={(items) => setDraft({ ...draft, targetLanguages: items })}
            placeholder={t('strategySections.placeholder.targetLanguages')}
            limits={BRAND_STRATEGY_LIMITS.targetLanguages}
          />
          <Select
            size="small"
            fullWidth
            value={draft.envergure || ''}
            onChange={(e) =>
              setDraft({ ...draft, envergure: (e.target.value as Envergure) || undefined })
            }
            displayEmpty
          >
            <MenuItem value="">{t('strategySections.envergure.undefined')}</MenuItem>
            {Object.entries(ENVERGURE_LABEL_KEYS).map(([val, labelKey]) => (
              <MenuItem key={val} value={val}>
                {t(labelKey)}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary">
            {t('strategySections.hint.envergure')}
          </Typography>
        </Box>
      );
    case 'existingAssets':
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label={t('strategySections.field.wikipediaUrl')}
            size="small"
            fullWidth
            value={draft.existingAssets?.wikipediaUrl || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                existingAssets: {
                  ...draft.existingAssets,
                  wikipediaUrl: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl),
                },
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.wikipediaUrl }}
          />
          <TextField
            label={t('strategySections.field.pressKitUrl')}
            size="small"
            fullWidth
            value={draft.existingAssets?.pressKitUrl || ''}
            onChange={(e) =>
              setDraft({
                ...draft,
                existingAssets: {
                  ...draft.existingAssets,
                  pressKitUrl: e.target.value.slice(0, BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl),
                },
              })
            }
            inputProps={{ maxLength: BRAND_STRATEGY_LIMITS.existingAssets.pressKitUrl }}
          />
          <ChipEditor
            label={t('strategySections.field.notableMentions')}
            items={draft.existingAssets?.notableMentions || []}
            onChange={(items) =>
              setDraft({
                ...draft,
                existingAssets: { ...draft.existingAssets, notableMentions: items },
              })
            }
            placeholder={t('strategySections.placeholder.notableMentions')}
            limits={BRAND_STRATEGY_LIMITS.existingAssets.notableMentions}
          />
        </Box>
      );
  }
}

export function LocationWizardEditor({
  locations,
  setLocations,
  suggestedLabel,
  zoneLimit,
  zonesUnlimited,
  totalIfAccepted,
  zonesLimitReached,
  tierLabel,
}: {
  locations: PendingLocation[];
  setLocations: React.Dispatch<React.SetStateAction<PendingLocation[]>>;
  suggestedLabel?: string;
  zoneLimit: number;
  zonesUnlimited: boolean;
  totalIfAccepted: number;
  zonesLimitReached: boolean;
  tierLabel: string;
}) {
  const { t } = useTranslation();
  const [label, setLabel] = useState('');
  const [zone, setZone] = useState('');
  const [scope, setScope] = useState<LocationScope | ''>('');

  const tierName = tierLabel.charAt(0).toUpperCase() + tierLabel.slice(1);

  const handleAdd = () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setLocations((prev) => [...prev, { label: trimmed, zone: zone.trim(), scope }]);
    setLabel('');
    setZone('');
    setScope('');
  };

  const handleUseSuggestion = () => {
    if (suggestedLabel && !label) {
      setLabel(suggestedLabel);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          {t('strategySections.locationWizard.quotaLabel', { tier: tierName })}
        </Typography>
        <Chip
          size="small"
          label={
            zonesUnlimited
              ? t('strategySections.locationWizard.quotaUnlimited', { count: totalIfAccepted })
              : `${totalIfAccepted} / ${zoneLimit}`
          }
          color={zonesLimitReached ? 'warning' : 'default'}
          variant="outlined"
        />
      </Box>

      {locations.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {locations.map((loc, i) => (
            <Chip
              key={`${loc.label}-${i}`}
              label={`${loc.label}${loc.scope ? ` (${SCOPE_LABELS[loc.scope]})` : ''}`}
              size="small"
              onDelete={() => setLocations((prev) => prev.filter((_, idx) => idx !== i))}
            />
          ))}
        </Box>
      )}

      {suggestedLabel && locations.length === 0 && !label && (
        <Button size="small" variant="text" onClick={handleUseSuggestion} sx={{ alignSelf: 'flex-start' }}>
          {t('strategySections.locationWizard.useSuggestion', { label: suggestedLabel })}
        </Button>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          label={t('strategySections.locationWizard.field.label')}
          size="small"
          fullWidth
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={t('strategySections.locationWizard.placeholder.label')}
          disabled={zonesLimitReached}
        />
        <TextField
          label={t('strategySections.locationWizard.field.zone')}
          size="small"
          fullWidth
          value={zone}
          onChange={(e) => setZone(e.target.value)}
          placeholder={t('strategySections.locationWizard.placeholder.zone')}
          disabled={zonesLimitReached}
        />
        <Select
          size="small"
          fullWidth
          value={scope}
          onChange={(e) => setScope(e.target.value as LocationScope | '')}
          displayEmpty
          disabled={zonesLimitReached}
        >
          <MenuItem value="">{t('strategySections.locationWizard.scopeOptional')}</MenuItem>
          {Object.entries(SCOPE_LABELS).map(([val, lbl]) => (
            <MenuItem key={val} value={val}>
              {lbl}
            </MenuItem>
          ))}
        </Select>
        <Button
          variant="outlined"
          onClick={handleAdd}
          disabled={!label.trim() || zonesLimitReached}
          startIcon={<AddIcon />}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('strategySections.locationWizard.addToList')}
        </Button>
        {zonesLimitReached && (
          <Alert severity="warning">
            {t('strategySections.locationWizard.limitReached', { tier: tierName })}
          </Alert>
        )}
      </Box>
    </Box>
  );
}

export function ChipEditor({
  label,
  items,
  onChange,
  placeholder,
  limits,
}: {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  limits?: { maxItems: number; maxCharsPerItem: number };
}) {
  const { t } = useTranslation();
  const [input, setInput] = useState('');
  const atCapacity = limits ? items.length >= limits.maxItems : false;
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
        {label}
      </Typography>
      {items.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {items.map((item, i) => (
            <Chip
              key={`${item}-${i}`}
              label={item}
              size="small"
              onDelete={() => onChange(items.filter((_, idx) => idx !== i))}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                py: 0.5,
                '& .MuiChip-label': {
                  whiteSpace: 'normal',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  lineHeight: 1.3,
                },
              }}
            />
          ))}
        </Box>
      )}
      <TextField
        fullWidth
        size="small"
        value={input}
        onChange={(e) =>
          setInput(limits ? e.target.value.slice(0, limits.maxCharsPerItem) : e.target.value)
        }
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const trimmed = input.trim();
            if (limits && (items.length >= limits.maxItems || trimmed.length > limits.maxCharsPerItem)) {
              setInput('');
              return;
            }
            if (trimmed && !items.includes(trimmed)) {
              onChange([...items, trimmed]);
            }
            setInput('');
          }
        }}
        placeholder={placeholder}
        disabled={atCapacity}
        inputProps={limits ? { maxLength: limits.maxCharsPerItem } : undefined}
        helperText={
          limits
            ? t('strategySections.chipHelper', {
                count: items.length,
                max: limits.maxItems,
                chars: limits.maxCharsPerItem,
              })
            : undefined
        }
      />
    </Box>
  );
}
